/**
 * `AISessionUsageTracker.ts` — port of Unity's `AISessionUsageTracker.cs`.
 *
 * Aggregates token usage per source ("Realtime", "Input Transcription",
 * Responses helpers) with a coarse USD estimate. Pricing entries cover only
 * the models the web slice actually uses; extend alongside new helpers.
 */

export interface UsageDelta {
  readonly model: string;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly inputAudioTokens?: number;
  readonly outputAudioTokens?: number;
  readonly cachedInputTokens?: number;
}

export interface UsageBucket {
  readonly source: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  inputAudioTokens: number;
  outputAudioTokens: number;
  cachedInputTokens: number;
  estimatedUsd: number;
}

interface ModelPricing {
  /** USD per 1M tokens. */
  readonly inputText: number;
  readonly outputText: number;
  readonly inputAudio: number;
  readonly outputAudio: number;
  readonly cachedInput: number;
}

/**
 * ⚠ ORDER IS PRECEDENCE. {@link priceFor} is `Array.find` on `startsWith`, so a
 * more specific prefix must sit ABOVE the generic one it refines — an entry
 * placed after `gpt-realtime` could never match a `gpt-realtime-*` id.
 */
const PRICING: ReadonlyArray<{ prefix: string; pricing: ModelPricing }> = [
  {
    // The whole `gpt-realtime` family, INCLUDING the 2.x models the stock
    // config moved to on 2026-08-28.
    //
    // Deliberately still ONE entry: 2.1 is priced here at the 1.5 tier, which
    // is an estimate, not a published figure. It is recorded as an estimate
    // rather than given a confident wrong number of its own — the same call the
    // `gpt-5.6` row below already makes for its audio rates. When real 2.x
    // rates land, add a `gpt-realtime-2` entry ABOVE this one (see the ordering
    // note) rather than editing these values, which still price 1.5 correctly.
    prefix: "gpt-realtime",
    pricing: { inputText: 4, outputText: 16, inputAudio: 32, outputAudio: 64, cachedInput: 0.4 },
  },
  {
    prefix: "gpt-4o-transcribe",
    pricing: { inputText: 2.5, outputText: 10, inputAudio: 6, outputAudio: 0, cachedInput: 0.3 },
  },
  {
    prefix: "gpt-5.5",
    pricing: { inputText: 1.25, outputText: 10, inputAudio: 0, outputAudio: 0, cachedInput: 0.125 },
  },
  {
    // Covers every gpt-5.6-* variant (the workflow-builder role defaults to
    // gpt-5.6-sol). Text rates are the published gpt-5.6 tier; audio rates
    // are a coarse estimate carried over from gpt-realtime until real
    // realtime-endpoint pricing for the 5.6 family lands.
    prefix: "gpt-5.6",
    pricing: { inputText: 1.75, outputText: 14, inputAudio: 32, outputAudio: 64, cachedInput: 0.175 },
  },
];

function priceFor(model: string): ModelPricing | null {
  const entry = PRICING.find((p) => model.startsWith(p.prefix));
  return entry?.pricing ?? null;
}

export class AISessionUsageTracker {
  private readonly m_buckets = new Map<string, UsageBucket>();

  add(source: string, delta: UsageDelta): void {
    let bucket = this.m_buckets.get(source);
    if (!bucket) {
      bucket = {
        source,
        model: delta.model,
        inputTokens: 0,
        outputTokens: 0,
        inputAudioTokens: 0,
        outputAudioTokens: 0,
        cachedInputTokens: 0,
        estimatedUsd: 0,
      };
      this.m_buckets.set(source, bucket);
    }
    bucket.model = delta.model || bucket.model;
    bucket.inputTokens += delta.inputTokens;
    bucket.outputTokens += delta.outputTokens;
    bucket.inputAudioTokens += delta.inputAudioTokens ?? 0;
    bucket.outputAudioTokens += delta.outputAudioTokens ?? 0;
    bucket.cachedInputTokens += delta.cachedInputTokens ?? 0;

    const pricing = priceFor(bucket.model);
    if (pricing) {
      const M = 1_000_000;
      bucket.estimatedUsd =
        (bucket.inputTokens / M) * pricing.inputText +
        (bucket.outputTokens / M) * pricing.outputText +
        (bucket.inputAudioTokens / M) * pricing.inputAudio +
        (bucket.outputAudioTokens / M) * pricing.outputAudio +
        (bucket.cachedInputTokens / M) * pricing.cachedInput;
    }
  }

  get buckets(): ReadonlyArray<UsageBucket> {
    return [...this.m_buckets.values()];
  }

  get totalTokens(): number {
    let total = 0;
    for (const b of this.m_buckets.values()) {
      total += b.inputTokens + b.outputTokens + b.inputAudioTokens + b.outputAudioTokens;
    }
    return total;
  }

  get totalEstimatedUsd(): number {
    let total = 0;
    for (const b of this.m_buckets.values()) total += b.estimatedUsd;
    return total;
  }

  /** One-line summary shown in the panel debug row. */
  get summaryText(): string {
    if (this.m_buckets.size === 0) return "";
    return `${this.totalTokens.toLocaleString()} tokens · ~$${this.totalEstimatedUsd.toFixed(3)}`;
  }

  get breakdownText(): string {
    return this.buckets
      .map(
        (b) =>
          `${b.source} (${b.model}): in ${b.inputTokens + b.inputAudioTokens}, out ${b.outputTokens + b.outputAudioTokens}, ~$${b.estimatedUsd.toFixed(4)}`,
      )
      .join("\n");
  }

  reset(): void {
    this.m_buckets.clear();
  }
}
